import React from "react";

import classNames from "classnames";

import { EmHouseIcon, EmSchoolIcon, EmStudentsIcon } from "@/components/icons-lib";
import {
  buildSummaryMetrics,
  type SummaryMetricKind,
  type SummaryMetricsLayout,
} from "@/providers/maps/utils/build-summary-metrics.utils";
import { StudentCountsInShapeColorByEnum } from "@/providers/store/api/shapes/enums/student-counts-in-shape.enum";

import { ClusterSummaryMetricCard } from "./cluster-selection/cluster-summary-metric-card";
import { SectionTitle } from "./section-title";
import styles from "./summary-metrics.module.css";

interface SummaryProps {
  studentsCount: number;
  dataCount: number;
  schoolsCount?: number;
  schoolsPlotted?: number;
  type: StudentCountsInShapeColorByEnum;
  schoolCountsOnly?: boolean;
}

function getMetricIcon(kind: SummaryMetricKind): React.ReactNode {
  switch (kind) {
    case "students":
    case "schools-attended":
      return <EmStudentsIcon />;
    case "schools-plotted":
      return <EmSchoolIcon />;
    case "grades":
    case "schools":
    default:
      return <EmHouseIcon />;
  }
}

function getGridClassName(layout: SummaryMetricsLayout): string {
  if (layout === "single") {
    return classNames(styles.summaryMetricsGrid, styles.summaryMetricsGridSingle);
  }

  return classNames(styles.summaryMetricsGrid, styles.summaryMetricsGridWide);
}

const SummaryComponent: React.FC<SummaryProps> = ({
  studentsCount,
  dataCount,
  schoolsCount = 0,
  schoolsPlotted = 0,
  type,
  schoolCountsOnly = true,
}) => {
  const { layout, metrics } = buildSummaryMetrics({
    schoolCountsOnly,
    type,
    studentsCount,
    dataCount,
    schoolsCount,
    schoolsPlotted,
  });

  return (
    <div className={styles.summaryMetrics}>
      <SectionTitle title="Summary" />
      <div className={getGridClassName(layout)}>
        {metrics.map((metric) => (
          <ClusterSummaryMetricCard
            key={metric.kind}
            className={
              metric.fullWidthOnWide ? styles.summaryMetricFullWidth : undefined
            }
            icon={getMetricIcon(metric.kind)}
            label={metric.label}
            value={metric.value}
          />
        ))}
      </div>
    </div>
  );
};

export const Summary = React.memo(SummaryComponent);
